import { setEnsightenVars } from 'lib/Ensighten';
import {
  call,
  put,
  take,
  actionChannel,
  select,
  takeEvery,
  fork,
  cancel,
} from 'redux-saga/effects';
import {
  clearTargeting,
  setPageTargeting,
  restrictDataProcessing,
  enableService,
  defineSlot,
  display,
  destroySlot,
  isInDeviceScreen,
} from 'lib/GoogleAds';

import ScreenSize from 'lib/ScreenSize';
import State from 'types/State';
import { isEmpty } from 'lodash';
import { SagaIterator } from 'redux-saga';
import * as CGLib from 'lib/CarGurusAds';
import { CarGurusElementId } from 'components/Ad/CGPixelAd';
import suggestiveSellingAdsSaga from './suggestiveSellingAdsSaga';

import {
  slotDestructionEnqueued,
  clearTargetingEnqueued,
  setPageTargetingEnqueued,
  dataProcessingRestrictionEnqueued,
  ensightenVarsSet,
  enableServiceEnqueued,
  slotDefinitionEnqueued,
  slotDisplayEnqueued,
  adComponentReadyToLoad,
  slotNotDisplayed,
  adComponentTearingdown,
  adComponentReadyToReload,
  slotNoLongerHasComponent,
  suggestiveSellingAdComponentReadyToLoad,
  adConfigReceived,
  adConfigUnloaded,
} from './googleAds';
import AdConfig from '../types/AdConfig';

import * as CGAdReduxImplementations from './carGurusAds';
import * as CarGurusAdConfig from '../types/CarGurusAdConfig';

function* configureCGPagewideAdsSaga({ payload }: Record<string, any>): SagaIterator {
  yield call<typeof clearTargeting>(clearTargeting);
  yield put(CGAdReduxImplementations.clearTargetingEnqueued());

  yield call<typeof setPageTargeting>(setPageTargeting, payload.adTargeting);
  yield put(CGAdReduxImplementations.setPageTargetingEnqueued(payload.adTargeting));

  yield call(enableService);
  yield put(CGAdReduxImplementations.enableServiceEnqueued());
}

function* configurePagewideAdsSaga({ payload }: Record<string, any>): SagaIterator {
  const wasRestricted: ReturnType<typeof restrictDataProcessing> = yield call(
    restrictDataProcessing
  );
  yield put(dataProcessingRestrictionEnqueued({ wasRestricted }));

  yield call<typeof clearTargeting>(clearTargeting);
  yield put(clearTargetingEnqueued());

  yield call<typeof setPageTargeting>(setPageTargeting, payload.adTargeting);
  yield put(setPageTargetingEnqueued(payload.adTargeting));

  const ensightenVars: ReturnType<typeof setEnsightenVars> = yield call<typeof setEnsightenVars>(
    setEnsightenVars,
    payload.adUnit,
    payload.adTargeting
  );
  yield put(ensightenVarsSet(ensightenVars));

  yield call(enableService);
  yield put(enableServiceEnqueued());
}

function* loadAdSaga({ payload }: { payload: AdConfig }): SagaIterator {
  const screenSizes = (payload.screenSizes || []).map((ss) => {
    return new ScreenSize(ss);
  });

  if (!isEmpty(screenSizes) && !isInDeviceScreen(screenSizes)) {
    yield put(
      slotNotDisplayed({
        config: payload,
        reason: 'screen sizes not in device screen',
      })
    );
    return;
  }

  const adUnitPath: string = yield select((state: State) => state.ads.adUnit);

  yield call(defineSlot, {
    adUnitPath,
    dimensions: payload.dimensions,
    elementId: payload.elementId,
    targeting: payload.targeting,
  });
  yield put(slotDefinitionEnqueued(payload));

  yield call(display, payload.elementId);
  yield put(slotDisplayEnqueued(payload));
}

function* loadCGAdSaga({ payload }: { payload: CarGurusAdConfig.default }): SagaIterator {
  const adUnitPath: string = yield select((state: State) => state.carGurusAds.adUnit);

  yield put(CGAdReduxImplementations.slotDefinitionEnqueued(payload));
  yield call(CGLib.defineSlot, {
    adUnitPath,
    elementId: CarGurusElementId,
    targeting: payload.targeting,
  });

  yield call(display, CarGurusElementId);
  yield put(CGAdReduxImplementations.slotDisplayEnqueued(payload));
}

function* destroyAd({ payload }: { payload: { elementId: string } }): SagaIterator {
  yield call(destroySlot, payload.elementId);
  yield put(slotDestructionEnqueued(payload));
}

function* reloadAdSaga({ payload }: { payload: AdConfig }): SagaIterator {
  yield call(destroyAd, { payload });
  yield call(loadAdSaga, { payload });
}

function* adsSaga(): SagaIterator {
  const cgAdsChannel: any = yield actionChannel(CGAdReduxImplementations.adComponentReadyToLoad);
  const individualAdsChannel: any = yield actionChannel(adComponentReadyToLoad);
  const suggestiveSellingAdsChannel: any = yield actionChannel(
    suggestiveSellingAdComponentReadyToLoad
  );

  while (true) {
    const action = yield take(adConfigReceived);
    if (action.payload.adsDisabled) {
      return;
    }

    const carGurusAction = yield take(CGAdReduxImplementations.adConfigReceived);

    yield call(configureCGPagewideAdsSaga, carGurusAction);
    yield call(configurePagewideAdsSaga, action);

    yield takeEvery(cgAdsChannel, loadCGAdSaga);
    yield takeEvery(individualAdsChannel, loadAdSaga);
    yield takeEvery(suggestiveSellingAdsChannel, suggestiveSellingAdsSaga);
    // not sure why TS was giving us errors here but it seems to work in practice
    yield takeEvery(adComponentTearingdown as any, destroyAd);
    yield takeEvery(adComponentReadyToReload as any, reloadAdSaga);
    yield takeEvery(slotNoLongerHasComponent, destroyAd);
  }
}

export function* rootSaga(): SagaIterator {
  while (true) {
    const adsTask = yield fork(adsSaga);

    yield take(adConfigUnloaded);

    yield cancel(adsTask);
  }
}
